import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import api from "../lib/api";
import { trUpper, fmtMoney, parseMoney, canonMoney } from "../lib/mw";
import { toast } from "sonner";

function FieldInput({ col, value, onChange, provinces, districts, onProvince }) {
  const base = "h-10 border-slate-700 bg-slate-800 text-slate-100 focus-visible:ring-blue-500";
  if (col.type === "select") {
    return (
      <select
        data-testid={`field-${col.key}`}
        className={`${base} w-full rounded-md px-3`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— SEÇİN —</option>
        {(col.options || []).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  if (col.type === "province") {
    return (
      <select
        data-testid={`field-${col.key}`}
        className={`${base} w-full rounded-md px-3`}
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); onProvince(e.target.value); }}
      >
        <option value="">— İL SEÇİN —</option>
        {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
    );
  }
  if (col.type === "district") {
    return (
      <select
        data-testid={`field-${col.key}`}
        className={`${base} w-full rounded-md px-3`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— İLÇE SEÇİN —</option>
        {districts.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
    );
  }
  return (
    <Input
      data-testid={`field-${col.key}`}
      type={col.type === "date" ? "date" : col.type === "number" || col.type === "currency" ? "text" : "text"}
      className={base}
      inputMode={col.type === "number" || col.type === "currency" ? "decimal" : undefined}
      value={col.type === "number" || col.type === "currency" ? fmtMoney(value) : value || ""}
      onChange={(e) => {
        let v = e.target.value;
        if (col.type === "number" || col.type === "currency") { onChange(parseMoney(v)); return; }
        if (col.type !== "email" && col.type !== "date" && col.type !== "number" && col.type !== "currency") v = trUpper(v);
        onChange(v);
      }}
    />
  );
}

export default function CustomerModal({ open, onOpenChange, columns, initial, onSaved }) {
  const [form, setForm] = useState({});
  const [archived, setArchived] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    api.get("/geo/provinces", { silent: true }).then((r) => setProvinces(r.data));
  }, []);

  useEffect(() => {
    if (open) {
      const d = { ...(initial?.data || {}) };
      (columns || []).forEach((c) => { if ((c.type === "currency" || c.type === "number") && d[c.key] !== undefined && d[c.key] !== "") d[c.key] = canonMoney(d[c.key]); });
      setForm(d);
      setArchived(!!initial?.archived);
      if (d.il) loadDistricts(d.il);
    }
  }, [open, initial]);

  const loadDistricts = async (prov) => {
    if (!prov) { setDistricts([]); return; }
    const { data } = await api.get(`/geo/districts/${encodeURIComponent(prov)}`, { silent: true });
    setDistricts(data);
  };

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const onProvince = (prov) => {
    setForm((f) => ({ ...f, ilce: "" }));
    loadDistricts(prov);
  };

  const save = async () => {
    try {
      if (initial?.id) {
        await api.put(`/customers/${initial.id}`, { data: form, archived });
      } else {
        await api.post("/customers", { data: form, archived });
      }
      toast.success("MÜŞTERİ KAYDEDİLDİ");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error("KAYIT HATASI");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto border-slate-700 bg-slate-900 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-head text-xl">
            {initial?.id ? `MÜŞTERİ DÜZENLE #${initial.id}` : "YENİ MÜŞTERİ"}
          </DialogTitle>
        </DialogHeader>
        {initial?.aktarilan?.length > 0 && (
          <div data-testid="customer-transferred-to" className="rounded-lg border border-sky-800 bg-sky-950/30 px-3 py-2 text-xs text-sky-200">
            <b>AKTARILDIĞI FİRMALAR:</b> {initial.aktarilan.map((a) => `${a.name} (${(a.at || "").slice(0, 10)})`).join(" · ")}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2 lg:grid-cols-3">
          {columns.map((col) => (
            <div key={col.key} className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-400">{col.label}</Label>
              <FieldInput
                col={col}
                value={form[col.key]}
                onChange={(v) => setField(col.key, v)}
                provinces={provinces}
                districts={districts}
                onProvince={onProvince}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 pt-2">
          <Switch data-testid="archive-switch" checked={archived} onCheckedChange={setArchived} />
          <Label className="text-sm text-slate-400">ARŞİVLENDİ</Label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700">
            VAZGEÇ
          </Button>
          <Button data-testid="customer-save-button" onClick={save} className="bg-blue-600 text-white hover:bg-blue-700">
            KAYDET
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
